import type { GTooltipCardData } from '~/presentation/ui';

import { EHeatmapScrollTooltipKey } from '../../types';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

type IHeatmapScrollTooltipValue = Record<EHeatmapScrollTooltipKey, GTooltipCardData>;

interface IHeatmapScrollTooltipProps {
  width?: GTooltipCardData['width'];
}
interface IHeatmapScrollTooltipResult {
  baseTooltip: IHeatmapScrollTooltipValue;
  getTooltip: (key: EHeatmapScrollTooltipKey) => GTooltipCardData | undefined;
}

export const useHeatmapScrollTooltip = (
  props?: IHeatmapScrollTooltipProps,
): IHeatmapScrollTooltipResult => {
  const { t } = useTranslation();
  const { width = 'wide' } = props ?? {};

  const baseTooltip: IHeatmapScrollTooltipValue = useMemo(
    () => ({
      [EHeatmapScrollTooltipKey.PERC_SCROLLED_DEPTH]: {
        title: t('Percentage scrolled'),
        content: t('The furthest point a visitor reached in a single session'),
        width,
      },
      [EHeatmapScrollTooltipKey.PERC_SCROLLED]: {
        title: t('Percentage scrolled'),
        content: t('Page are divided into depth ranges'),
        width,
      },
      [EHeatmapScrollTooltipKey.TOTAL_VISITOR]: {
        title: t('Number of visitors'),
        content: t('How many visitors scrolled to that point'),
        width,
      },
      [EHeatmapScrollTooltipKey.PERC_DROP_OFF]: {
        title: t('Percentage drop off'),
        content: t('% of visitors who did not reach that point'),
        width,
      },
      [EHeatmapScrollTooltipKey.AVG_TIME_SPENT]: {
        title: t('Average time spent'),
        content: t('Average time visitors spent at this scroll point'),
        width,
      },
      [EHeatmapScrollTooltipKey.CONVERSION_RATE]: {
        title: t('Conversion rate'),
        content: t('% of visitors who scrolled to that point and completed an order'),
        width,
      },
      [EHeatmapScrollTooltipKey.PERC_OF_SESSION_LENGTH]: {
        title: t('Percentage of session length'),
        content: t('% of time spent at that scroll point'),
        width,
      },
      [EHeatmapScrollTooltipKey.TOTAL_VISITORS_CONVERTED]: {
        title: t('Number of visitors converted'),
        content: t('Number of visitors who reached this point and made a purchase'),
        width,
      },
      [EHeatmapScrollTooltipKey.TOTAL_REVENUE]: {
        title: t('Revenue'),
        content: t(
          'Total revenue from sessions where this position was the <span class="Polaris-Text--root Polaris-Text--bodyMd Polaris-Text--semibold">deepest scroll point</span>',
        ),
        width,
      },
    }),
    [t, width],
  );

  const getTooltip = useCallback(
    (key: EHeatmapScrollTooltipKey): GTooltipCardData | undefined => {
      return baseTooltip[key];
    },
    [baseTooltip],
  );

  return { baseTooltip, getTooltip };
};
